import React, { useState, useEffect } from "react";
import {
  FiChevronDown,
  FiChevronUp,
  FiDownload,
  FiFilter,
  FiRefreshCw,
  FiZap,
  FiClock,
  FiCpu,
  FiActivity,
  FiDollarSign,
} from "react-icons/fi";
import "../assets/css/token-inspector.css";

// Cost is now calculated by backend (per-model pricing from config/llm-pricing.json)
// Frontend just displays the pre-calculated cost_usd from API

const formatCost = (cost) => {
  if (cost === null || cost === undefined || isNaN(cost)) return "-";
  if (cost === 0) return "$0";
  if (cost < 0.0001) return `$${cost.toFixed(6)}`;
  if (cost < 0.01) return `$${cost.toFixed(4)}`;
  return `$${cost.toFixed(3)}`;
};

const NEXT_API_URL = process.env.REACT_APP_NEXT_API_URL || "http://localhost:3000";
const PAGE_SIZE = 10;

// Time periods for cost summary table
const TIME_PERIODS = [
  { label: "10 min", minutes: 10 },
  { label: "30 min", minutes: 30 },
  { label: "1 hour", minutes: 60 },
  { label: "6 hours", minutes: 360 },
  { label: "12 hours", minutes: 720 },
  { label: "1 day", minutes: 1440 },
  { label: "2 days", minutes: 2880 },
  { label: "7 days", minutes: 10080 },
  { label: "30 days", minutes: 43200 },
];

function TokenInspector({ userId }) {
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState({ total: 0, offset: 0, limit: 10, hasMore: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [filters, setFilters] = useState({ tier: "", model: "", minTokens: "" });
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [costSummary, setCostSummary] = useState([]);
  const [costLoading, setCostLoading] = useState(false);

  const fetchData = async (page = 1) => {
    if (!userId) return;

    setLoading(true);
    setError(null);

    try {
      const offset = (page - 1) * PAGE_SIZE;
      const params = new URLSearchParams({ 
        user_id: userId, 
        limit: PAGE_SIZE.toString(),
        offset: offset.toString()
      });
      if (filters.tier) params.append("tier", filters.tier);
      if (filters.model) params.append("model", filters.model);
      if (filters.minTokens) params.append("min_tokens", filters.minTokens);

      const res = await fetch(`${NEXT_API_URL}/api/usage/token-inspector?${params}`);
      if (!res.ok) throw new Error("Failed to fetch");

      const json = await res.json();
      if (json.success) {
        setData(json.data || []);
        setSummary(json.summary || null);
        setPagination(json.pagination || { total: 0, offset: 0, limit: 10, hasMore: false });
      } else {
        throw new Error(json.error || "Unknown error");
      }
    } catch (err) {
      console.error("TokenInspector fetch error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch cost summary for all time periods
  const fetchCostSummary = async () => {
    if (!userId) return;
    setCostLoading(true);
    
    try {
      const results = await Promise.all(
        TIME_PERIODS.map(async (period) => {
          const params = new URLSearchParams({
            user_id: userId,
            minutes: period.minutes.toString(),
          });
          
          const res = await fetch(`${NEXT_API_URL}/api/usage/cost-summary?${params}`);
          if (!res.ok) return { ...period, queries: 0, totalTokens: 0, totalCost: 0 };
          
          const json = await res.json();
          if (json.success) {
            return {
              ...period,
              queries: json.data?.queries || 0,
              totalTokens: json.data?.totalTokens || 0,
              promptTokens: json.data?.promptTokens || 0,
              completionTokens: json.data?.completionTokens || 0,
              totalCost: json.data?.totalCost || 0,
            };
          }
          return { ...period, queries: 0, totalTokens: 0, totalCost: 0 };
        })
      );
      setCostSummary(results);
    } catch (err) {
      console.error("Cost summary fetch error:", err);
    } finally {
      setCostLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1);
    fetchCostSummary();
    setCurrentPage(1);
  }, [userId]);

  const handleApplyFilters = () => {
    setCurrentPage(1);
    fetchData(1);
  };

  const handleClearFilters = () => {
    setFilters({ tier: "", model: "", minTokens: "" });
    setCurrentPage(1);
    setTimeout(() => fetchData(1), 0);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchData(newPage);
  };

  const toggleRowExpand = (id) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const exportCSV = () => {
    const headers = [
      "Time",
      "Query",
      "Model",
      "Tier",
      "Prompt Tokens",
      "Completion Tokens",
      "Total Tokens",
      "Cost ($)",
      "Latency (ms)",
      "TTFT (ms)",
      "Tokens/sec",
      "Topic",
    ];

    const rows = data.map((r) => [
      r.createdAt,
      `"${(r.queryText || "").replace(/"/g, '""').substring(0, 500)}"`,
      r.model,
      r.tier,
      r.promptTokens || 0,
      r.completionTokens || 0,
      r.totalTokens || 0,
      (r.costUsd || 0).toFixed(6),
      r.latencyMs || 0,
      r.ttftMs || 0,
      r.tokensPerSec || 0,
      r.sessionName || "",
    ]);

    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `token-inspector-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "-";
    return num.toLocaleString();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const truncateText = (text, maxLen = 80) => {
    if (!text) return "-";
    return text.length > maxLen ? text.substring(0, maxLen) + "..." : text;
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case "full": return "teal";
      case "standard": return "blue";
      case "lite": return "orange";
      case "minimal": return "gray";
      default: return "gray";
    }
  };



  if (loading && data.length === 0) {
    return (
      <div className="token-inspector">
        <div className="token-inspector-loading">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="token-inspector">
        <div className="token-inspector-error">
          Error: {error}
          <button onClick={() => fetchData(currentPage)} className="ti-btn ti-btn-sm">
            <FiRefreshCw size={14} /> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="token-inspector">
      {/* Header */}
      <div className="ti-header">
        <div className="ti-title">
          <FiCpu size={20} />
          <h5>Token Inspector</h5>
          <span className="ti-badge">{data.length} queries</span>
        </div>
        <div className="ti-actions">
          <button
            className={`ti-btn ti-btn-sm ${showFilters ? "active" : ""}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FiFilter size={14} /> Filters
          </button>
          <button className="ti-btn ti-btn-sm" onClick={() => fetchData(currentPage)} disabled={loading}>
            <FiRefreshCw size={14} className={loading ? "spin" : ""} />
          </button>
          <button className="ti-btn ti-btn-sm" onClick={exportCSV}>
            <FiDownload size={14} /> CSV
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      {summary && (
        <div className="ti-summary">
          <div className="ti-stat">
            <FiActivity className="ti-stat-icon teal" />
            <div>
              <span className="ti-stat-value">{formatNumber(summary.avgPromptTokens)}</span>
              <span className="ti-stat-label">Avg Prompt</span>
            </div>
          </div>
          <div className="ti-stat">
            <FiZap className="ti-stat-icon orange" />
            <div>
              <span className="ti-stat-value">{formatNumber(summary.avgCompletionTokens)}</span>
              <span className="ti-stat-label">Avg Completion</span>
            </div>
          </div>
          <div className="ti-stat">
            <FiCpu className="ti-stat-icon blue" />
            <div>
              <span className="ti-stat-value">{formatNumber(summary.avgTotalTokens)}</span>
              <span className="ti-stat-label">Avg Total</span>
            </div>
          </div>
          <div className="ti-stat">
            <FiDollarSign className="ti-stat-icon green" />
            <div>
              <span className="ti-stat-value">{formatCost(summary.avgCost)}</span>
              <span className="ti-stat-label">Avg Cost</span>
            </div>
          </div>
          <div className="ti-stat">
            <FiClock className="ti-stat-icon purple" />
            <div>
              <span className="ti-stat-value">{formatNumber(summary.avgLatency)}ms</span>
              <span className="ti-stat-label">Avg Latency</span>
            </div>
          </div>
        </div>
      )}

      {/* Max Tokens Alert */}
      {summary?.maxTokensQuery && (
        <div className="ti-max-alert">
          <strong>Highest Token Query:</strong> {formatNumber(summary.maxTokensQuery.totalTokens)} tokens
          <span className="ti-max-preview">"{truncateText(summary.maxTokensQuery.queryPreview, 60)}"</span>
        </div>
      )}

      {/* Cost Summary Table by Time Period */}
      <div className="ti-cost-summary">
        <div className="ti-cost-header">
          <FiDollarSign size={18} />
          <h6>Cost Summary by Time Period</h6>
          <button 
            className="ti-btn ti-btn-sm" 
            onClick={fetchCostSummary} 
            disabled={costLoading}
            title="Refresh cost summary"
          >
            <FiRefreshCw size={12} className={costLoading ? "spin" : ""} />
          </button>
        </div>
        <div className="ti-cost-table-wrapper">
          <table className="ti-cost-table">
            <thead>
              <tr>
                <th>Time Period</th>
                <th>Queries</th>
                <th>Prompt Tokens</th>
                <th>Completion Tokens</th>
                <th>Total Tokens</th>
                <th>Total Cost</th>
              </tr>
            </thead>
            <tbody>
              {costLoading ? (
                <tr>
                  <td colSpan="6" className="ti-loading-cell">Loading...</td>
                </tr>
              ) : costSummary.length === 0 ? (
                <tr>
                  <td colSpan="6" className="ti-empty">No data</td>
                </tr>
              ) : (
                costSummary.map((row, idx) => (
                  <tr key={idx} className={row.queries > 0 ? "" : "ti-row-empty"}>
                    <td className="ti-period">{row.label}</td>
                    <td className="ti-num">{formatNumber(row.queries)}</td>
                    <td className="ti-num">{formatNumber(row.promptTokens)}</td>
                    <td className="ti-num">{formatNumber(row.completionTokens)}</td>
                    <td className="ti-num ti-total">{formatNumber(row.totalTokens)}</td>
                    <td className="ti-num ti-cost">{formatCost(row.totalCost)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="ti-filters">
          <div className="ti-filter-group">
            <label>Tier</label>
            <select
              value={filters.tier}
              onChange={(e) => setFilters({ ...filters, tier: e.target.value })}
            >
              <option value="">All</option>
              <option value="minimal">Minimal</option>
              <option value="lite">Lite</option>
              <option value="standard">Standard</option>
              <option value="full">Full</option>
            </select>
          </div>
          <div className="ti-filter-group">
            <label>Model</label>
            <input
              type="text"
              placeholder="e.g. claude, gpt"
              value={filters.model}
              onChange={(e) => setFilters({ ...filters, model: e.target.value })}
            />
          </div>
          <div className="ti-filter-group">
            <label>Min Tokens</label>
            <input
              type="number"
              placeholder="e.g. 10000"
              value={filters.minTokens}
              onChange={(e) => setFilters({ ...filters, minTokens: e.target.value })}
            />
          </div>
          <div className="ti-filter-actions">
            <button className="ti-btn ti-btn-primary" onClick={handleApplyFilters}>
              Apply
            </button>
            <button className="ti-btn" onClick={handleClearFilters}>
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="ti-table-wrapper">
        <table className="ti-table">
          <thead>
            <tr>
              <th></th>
              <th>Time</th>
              <th>Query</th>
              <th>Model</th>
              <th>Tier</th>
              <th>Prompt</th>
              <th>Completion</th>
              <th>Total</th>
              <th>Cost</th>
              <th>Latency</th>
              <th>Tok/s</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan="11" className="ti-empty">No data found</td>
              </tr>
            ) : (
              data.map((row) => (
                <React.Fragment key={row.id}>
                  <tr
                    className={`ti-row ${expandedRows.has(row.id) ? "expanded" : ""}`}
                    onClick={() => toggleRowExpand(row.id)}
                  >
                    <td className="ti-expand-cell">
                      {expandedRows.has(row.id) ? <FiChevronUp /> : <FiChevronDown />}
                    </td>
                    <td className="ti-time">{formatDate(row.createdAt)}</td>
                    <td className="ti-query">{truncateText(row.queryText, 50)}</td>
                    <td className="ti-model" title={row.model}>
                      {row.model?.split("-").slice(-2).join("-") || "-"}
                    </td>
                    <td>
                      <span className={`ti-tier-badge ${getTierColor(row.tier)}`}>
                        {row.tier}
                      </span>
                    </td>
                    <td className="ti-num">{formatNumber(row.promptTokens)}</td>
                    <td className="ti-num">{formatNumber(row.completionTokens)}</td>
                    <td className="ti-num ti-total">{formatNumber(row.totalTokens)}</td>
                    <td className="ti-num ti-cost">{formatCost(row.costUsd)}</td>
                    <td className="ti-num">{row.latencyMs ? `${formatNumber(row.latencyMs)}ms` : "-"}</td>
                    <td className="ti-num">{row.tokensPerSec || "-"}</td>
                  </tr>
                  {expandedRows.has(row.id) && (
                    <tr className="ti-expanded-row">
                      <td colSpan="11">
                        <div className="ti-expanded-content">
                          <div className="ti-expanded-section">
                            <h6>Query</h6>
                            <pre>{row.queryText || "N/A"}</pre>
                          </div>
                          <div className="ti-expanded-section">
                            <h6>Response Preview</h6>
                            <pre>{truncateText(row.responseText, 500) || "N/A"}</pre>
                          </div>
                          <div className="ti-expanded-meta">
                            <span><strong>Topic:</strong> {row.sessionName || "N/A"}</span>
                            <span><strong>TTFT:</strong> {row.ttftMs ? `${row.ttftMs}ms` : "N/A"}</span>
                            <span><strong>Finish:</strong> {row.finishReason || "N/A"}</span>
                            <span><strong>Provider:</strong> {row.provider}</span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.total > 0 && (
        <div className="ti-pagination">
          <span>
            Showing {pagination.offset + 1}-{Math.min(pagination.offset + data.length, pagination.total)} of {pagination.total}
          </span>
          <div className="ti-pagination-btns">
            <button
              className="ti-btn ti-btn-sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1 || loading}
            >
              Prev
            </button>
            <span style={{ padding: "0 8px" }}>Page {currentPage}</span>
            <button
              className="ti-btn ti-btn-sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!pagination.hasMore || loading}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TokenInspector;
